import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAppConfigRequest } from "../../redux/Dashboard/dashboardSlice";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";
import GatewaySelectionDialog from "../Common/PaymentGatewaySelectionDialog";
import {
  getPaymentPlatform,
  normalizeGateway,
  savePaymentMeta,
  startPaymentFromOrderResponse,
} from "../Common/PaymentGatewayMethods";
import {
  addJewelleryToCartRequest,
  checkoutJewelleryCartRequest,
  fetchJewelleryCartRequest,
  removeJewelleryFromCartRequest,
  resetJewelleryCheckout,
} from "../../redux/Jewellery/jewelleryCartSlice";
import {
  cartTotals,
  formatMoney,
  jewelleryPurchaseModes,
  linePayable,
  priceBreakdown,
} from "./jewelleryUtils";

const columns = "minmax(320px, 2.2fr) 170px 180px minmax(220px, 0.9fr)";

const QtyStepper = ({ item, onChange, border }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      width: "fit-content",
      justifySelf: "start",
      border: `1px solid ${border}`,
      borderRadius: 2,
      height: 44,
    }}
  >
    <IconButton
      aria-label="Decrease quantity"
      size="small"
      disabled={item.quantity < 1}
      onClick={() => onChange(item.quantity - 1)}
      sx={{ width: 40, height: 42, borderRadius: 0, color: "inherit" }}
    >
      <RemoveIcon sx={{ fontSize: 20 }} />
    </IconButton>
    <Typography sx={{ minWidth: 28, textAlign: "center", fontWeight: 700, fontSize: 18 }}>
      {item.quantity}
    </Typography>
    <IconButton
      aria-label="Increase quantity"
      size="small"
      disabled={item.quantity >= item.maxQty}
      onClick={() => onChange(item.quantity + 1)}
      sx={{ width: 40, height: 42, borderRadius: 0, color: "inherit" }}
    >
      <AddIcon sx={{ fontSize: 20 }} />
    </IconButton>
  </Box>
);

const ItemThumb = ({ item }) => {
  const [failed, setFailed] = useState(false);
  return (
    <Box
      component="img"
      src={failed || !item.image ? "/images/logo.png" : item.image}
      alt=""
      onError={() => setFailed(true)}
      sx={{
        width: 96,
        height: 96,
        objectFit: "cover",
        borderRadius: 1,
        flexShrink: 0,
        bgcolor: "rgba(255,255,255,0.08)",
      }}
    />
  );
};

export default function JewelleryCart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeColors = useAppTheme();
  const { symbol } = useCurrency();
  const { items = [], checkingOut, checkout } = useSelector(
    (state) => state.jewelleryCart || {},
  );
  const { appConfig } = useSelector((state) => state.dashboard || {});
  const paymentGateways = appConfig?.paymentGateways || [];
  const { online: canBuyOnline, offline: canBuyOffline } =
    jewelleryPurchaseModes(appConfig);
  const totals = useMemo(() => cartTotals(items), [items]);
  const lineColor = "rgba(255,255,255,0.16)";
  const offlineSnapshot = useRef(null);
  const [openGatewayDialog, setOpenGatewayDialog] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState("");
  const [payChoiceOpen, setPayChoiceOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchJewelleryCartRequest());
    dispatch(getAppConfigRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!checkout?.success) return;
    const result = checkout.data || {};
    if (result.paymentType === "Online" && result.payment) {
      if (!selectedGateway) return;
      savePaymentMeta(
        {
          grams: 0,
          metal: "Jewellery",
          metalName: "Jewellery",
          amount: result.totalAmount,
          totalAmount: result.totalAmount,
          selectedGateway,
          orderType: "Purchase",
          buyType: "Online",
          planid: "",
          moneysip: false,
        },
        { replace: true },
      );
      startPaymentFromOrderResponse(
        { success: true, message: checkout.message, data: result.payment },
        selectedGateway,
      );
    }
    if (result.paymentType === "Offline") {
      const orders = Array.isArray(result.orders) ? result.orders : [];
      const snapshot = offlineSnapshot.current || { items: [], tax: 0 };
      const names = [
        ...new Set(snapshot.items.map((item) => item.name).filter(Boolean)),
      ];
      const quantity = snapshot.items.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
      );
      navigate("/artificialJewelleryOfflinePayment", {
        state: {
          flow: "jewellery",
          appConfig,
          offlineOrderData: {
            orderId: orders[0]?.orderId,
            orderIds: orders.map((order) => order.orderId).filter(Boolean),
            metalName: "Jewellery",
            jewelleryName: names.length === 1 ? names[0] : names.join(", ") || "Jewellery",
            quantity: quantity || orders.length || 1,
            finalAmount: result.totalAmount,
            tax: Number(snapshot.tax || 0),
            weight: 0,
          },
        },
      });
    }
    dispatch(resetJewelleryCheckout());
  }, [checkout, dispatch, navigate, selectedGateway, appConfig]);

  const changeQuantity = (item, next) => {
    if (next > item.quantity) {
      dispatch(
        addJewelleryToCartRequest({
          jewellery: item.jewelleryId,
          inventory: item.inventoryId,
          quantity: next - item.quantity,
        }),
      );
      return;
    }
    if (next < item.quantity) {
      dispatch(
        removeJewelleryFromCartRequest({
          inventory: item.inventoryId,
          quantity: item.quantity - next,
        }),
      );
    }
  };

  const startOnline = () => {
    setPayChoiceOpen(false);
    if (!paymentGateways.length) {
      toast.warn("No payment gateways configured!");
      return;
    }
    if (paymentGateways.length === 1) {
      placeOnline(paymentGateways[0]);
      return;
    }
    setOpenGatewayDialog(true);
  };

  const placeOnline = (gateway) => {
    setOpenGatewayDialog(false);
    if (!gateway) return;
    const normalized = normalizeGateway(gateway);
    setSelectedGateway(normalized);
    dispatch(
      checkoutJewelleryCartRequest({
        paymentType: "Online",
        transactionMode: normalized,
        platform: getPaymentPlatform(),
        x_api_version: "2022-09-01",
      }),
    );
  };

  const placeOffline = () => {
    setPayChoiceOpen(false);
    offlineSnapshot.current = {
      items: items.map((item) => ({ name: item.name, quantity: item.quantity })),
      tax: totals.tax,
    };
    dispatch(checkoutJewelleryCartRequest({ paymentType: "Offline" }));
  };

  const startCheckout = () => {
    if (canBuyOnline && !canBuyOffline) {
      startOnline();
      return;
    }
    if (canBuyOffline && !canBuyOnline) {
      placeOffline();
      return;
    }
    setPayChoiceOpen(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        p: { xs: 2, md: 3 },
        color: themeColors.text,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1280, mx: "auto" }}>
        <Button
          onClick={() => navigate("/artificialJewellery")}
          sx={{ mb: 0.5, px: 0, textTransform: "none", color: themeColors.primary, fontSize: 16 }}
        >
          Continue shopping
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: themeColors.primary }}>
          Cart
        </Typography>

        {!items.length ? (
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              background: themeColors.bigContainer,
              color: "#fff",
              border: `1px solid ${lineColor}`,
            }}
          >
            <Typography sx={{ mb: 2 }}>Your cart is empty</Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/artificialJewellery")}
              sx={{
                textTransform: "none",
                background: "#fff",
                color: "#111",
                "&:hover": { background: "#f2f2f2" },
              }}
            >
              Browse artificial jewellery
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              background: themeColors.bigContainer,
              color: "#fff",
              border: `1px solid ${lineColor}`,
              borderRadius: 1,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 760 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: columns,
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${lineColor}`,
                    color: "inherit",
                    opacity: 0.7,
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  <span>Item</span>
                  <span>Price</span>
                  <span>Qty</span>
                  <span>Total</span>
                </Box>

                {items.map((item) => {
                  const price = priceBreakdown(item, item.quantity);
                  return (
                    <Box
                      key={item.jewelleryId}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: columns,
                        alignItems: "center",
                        px: 3,
                        py: 2.25,
                        borderBottom: `1px solid ${lineColor}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0, pr: 2 }}>
                        <ItemThumb item={item} />
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1.25, minWidth: 0 }}
                            >
                              {item.name}
                            </Typography>
                            {Number(item.discount) > 0 && (
                              <Typography
                                sx={{
                                  color: "#3DDC84",
                                  fontWeight: 800,
                                  fontSize: 18,
                                  whiteSpace: "nowrap",
                                  flexShrink: 0,
                                }}
                              >
                                ↓ {Number(item.discount)}%
                              </Typography>
                            )}
                          </Box>
                          {item.caption ? (
                            <Typography
                              sx={{ mt: 0.5, fontSize: 14, opacity: 0.72, whiteSpace: "normal" }}
                            >
                              {item.caption}
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                      <Box>
                        {Number(item.discount) > 0 && (
                          <Typography
                            sx={{
                              fontSize: 14,
                              textDecoration: "line-through",
                              opacity: 0.55,
                            }}
                          >
                            {formatMoney(symbol, item.mrp)}
                          </Typography>
                        )}
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: themeColors.amountOption }}>
                          {formatMoney(symbol, price.unitDiscounted)}
                        </Typography>
                      </Box>
                      <QtyStepper
                        item={item}
                        border={lineColor}
                        onChange={(next) => changeQuantity(item, next)}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 0.5 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 22 }}>
                          {formatMoney(symbol, linePayable(item))}
                        </Typography>
                        <IconButton
                          aria-label="Remove from cart"
                          onClick={() =>
                            dispatch(
                              removeJewelleryFromCartRequest({ inventory: item.inventoryId }),
                            )
                          }
                          sx={{ color: "inherit", opacity: 0.75 }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                px: 3,
                py: 2.5,
              }}
            >
              <Typography sx={{ fontSize: 18, opacity: 0.8 }}>
                Discount − {formatMoney(symbol, totals.discountAmount)} · Tax{" "}
                {formatMoney(symbol, totals.tax)}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, ml: "auto" }}>
                <Typography sx={{ fontWeight: 800, fontSize: 32, }}>
                  {formatMoney(symbol, totals.payable)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={checkingOut}
                  onClick={startCheckout}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 4,
                    py: 1.4,
                    fontSize: 18,
                    background: "#fff",
                    color: "#111",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { background: "#f2f2f2", boxShadow: "none" },
                  }}
                >
                  {checkingOut ? "Placing order..." : "Checkout"}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      <GatewaySelectionDialog
        open={openGatewayDialog}
        onClose={placeOnline}
        gateways={paymentGateways}
      />
      <Dialog
        open={payChoiceOpen}
        onClose={() => setPayChoiceOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: themeColors.bigContainer,
            color: themeColors.text,
            border: `1px solid ${themeColors.scaffold}`,
          },
        }}
      >
        <DialogTitle sx={{ color: themeColors.primary, textAlign: "center" }}>
          Buy artificial jewellery
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pb: 1 }}>
            {canBuyOnline && (
            <Button
              variant="contained"
              disabled={checkingOut}
              onClick={() => {
                setPayChoiceOpen(false);
                startOnline();
              }}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                py: 1.2,
                background: themeColors.primary,
                color: themeColors.text,
              }}
            >
              Online Buy
            </Button>
            )}
            {canBuyOffline && (
            <Button
              variant="contained"
              disabled={checkingOut}
              onClick={placeOffline}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                py: 1.2,
                background: themeColors.primary,
                color: themeColors.text,
              }}
            >
              Offline Buy
            </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
